// src/components/lead/sections/ContactCard.tsx
  import type { ExtractedLeadV2, Lead } from "../../../types/lead";
  import type { UpdateLeadFieldFn } from "../../../types/leadActions";

  import { InterestTimelineSection } from "./InterestTimelineSection";
  import { ContactCardFormAction } from "./ContactCardFormAction";

  type Props = {
    lead: Lead;
    e: ExtractedLeadV2;
    phoneDraft: string;
    setPhoneDraft: (value: string) => void;
    normalizePhoneOrEmpty: (value: string) => string;
    updateCurrentLeadField: UpdateLeadFieldFn;
    setToast: (t: { message: string; type: "success" | "error" } | null) => void;
  };

  export function ContactCard(props: Props) {
    const {
      lead,
      e,
      phoneDraft,
      setPhoneDraft,
      normalizePhoneOrEmpty,
      updateCurrentLeadField,
      setToast,
    } = props;

    return (
      /* Contact card */
      <div data-app-panel="ContactCard" className="bg-gray-50 rounded-2xl p-4 border border-gray-200 dark:bg-zinc-900/40 dark:border-zinc-700">
        {/* Header row */}
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between mb-3 min-w-0">
          <h3 className="text-xl font-semibold shrink-0">Contact</h3>
          <div className="flex shrink-0 flex-col items-stretch sm:items-end gap-1 min-w-0 max-w-full">
            <ContactCardFormAction lead={lead} setToast={setToast} />
            <span className="text-xs text-gray-400 uppercase text-right">Required</span>
          </div>
        </div>

        {/* Contact fields grid */}
        <div className="grid grid-cols-1 gap-3">
          {/* Customer name */}
          <div>
            <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">
              Customer Name
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                className="w-full sm:flex-1 border rounded-lg px-3 py-2 text-base"
                placeholder="First name"
                value={e.first_name || ""}
                onChange={(ev) =>
                  updateCurrentLeadField("first_name", ev.target.value)
                }
              />
              <input
                className="w-full sm:flex-1 border rounded-lg px-3 py-2 text-base"
                placeholder="Last name"
                value={e.last_name || ""}
                onChange={(ev) =>
                  updateCurrentLeadField("last_name", ev.target.value)
                }
              />
            </div>
          </div>

          {/* Phone */}
          <div>
            <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">
              Phone
            </div>
            <input
              data-lead-field="phone"
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="Phone"
              value={phoneDraft}
              onChange={(ev) => {
                // Allow user to type freely; optionally strip non-digits if you want:
                const raw = ev.target.value;
                const digitsOnly = raw.replace(/[^\d]/g, "");
                setPhoneDraft(digitsOnly);
              }}
              onBlur={() => {
                const normalized = normalizePhoneOrEmpty(phoneDraft);
                // If length isn't 7 or 10, clear it (your requested behavior)
                setPhoneDraft(normalized);
                updateCurrentLeadField("phone", normalized || null);
              }}
            />
          </div>

          {/* Email */}
          <div>
            <div className="text-gray-500 text-xs uppercase tracking-wide mb-1">
              Email
            </div>
            <input
              className="w-full border rounded-lg px-3 py-2 text-sm"
              placeholder="Email"
              value={e.email || ""}
              onChange={(ev) => updateCurrentLeadField("email", ev.target.value)}
            />
          </div>

          {/* Interest level & timeline */}
          <InterestTimelineSection
            e={e}
            updateCurrentLeadField={updateCurrentLeadField}
          />
        </div>
      </div>
    );
  }
  